import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { Missing, DateCell, TextCell } from "@tfg/ui"
import { useMemo } from "react"

import { FindIngestionsResponseDto } from "~/gen/api"
import { IngestionLaunchMenu } from "~/pages/Admin/Ingestion/ProductImport/components/IngestionLaunchMenu"
import { IngestionStatusBadge } from "~/pages/Admin/Ingestion/ProductImport/components/IngestionStatusBadge"
import { useExportProductQrCodeMutation } from "~/pages/Admin/Ingestion/ProductImport/hooks/useExportProductQrCodeMutation"
import { useRevertImportMutation } from "~/pages/Admin/Ingestion/ProductImport/hooks/useRevertImportMutation"

type Props = {
  handleErrorModalOpen: (ingestionId: string) => void
  handleImportModalOpen: (
    ingestion: FindIngestionsResponseDto,
    isBlankIngestion: boolean
  ) => void
}

export const useIngestionListColumns = ({
  handleErrorModalOpen,
  handleImportModalOpen,
}: Props) => {
  const columnHelper = createColumnHelper<FindIngestionsResponseDto>()

  const { mutate: revertImport } = useRevertImportMutation()
  const { mutate: exportProductQrCode } = useExportProductQrCodeMutation()

  return useMemo<ColumnDef<FindIngestionsResponseDto, any>[]>(
    () => [
      columnHelper.accessor("createdAt", {
        header: "Created at",
        cell: (info) => <DateCell date={info.getValue()} />,
      }),
      columnHelper.accessor("createdBy.email", {
        header: "Email",
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("document.originalFileName", {
        header: "File Name",
        cell: (info) => {
          const value = info.getValue()
          return value ? <TextCell value={info.getValue()} /> : <Missing />
        },
      }),
      columnHelper.accessor("product_count", {
        header: "No. products ingested",
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("traceabilityLaunchedAt", {
        header: "Traceability Launch date",
        cell: (info) => <DateCell date={info.getValue()} />,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          return <IngestionStatusBadge status={info.getValue()} />
        },
      }),
      columnHelper.display({
        id: "action-cell-launch",
        cell: ({ row }) => {
          return (
            <IngestionLaunchMenu
              row={row}
              handleErrorModalOpen={handleErrorModalOpen}
              handleImportModalOpen={handleImportModalOpen}
              revertImport={revertImport}
              exportProductQrCode={exportProductQrCode}
            />
          )
        },
      }),
    ],
    [
      columnHelper,
      exportProductQrCode,
      handleErrorModalOpen,
      revertImport,
      handleImportModalOpen,
    ]
  )
}
